import { supabase } from "@/lib/supabase"

export interface AnalyticsSummary {
  total_views: number
  unique_visitors: number
  average_duration: number
  top_countries: Array<{ country: string; count: number }>
  top_devices: Array<{ device: string; count: number }>
  views_by_day: Array<{ date: string; views: number }>
}

export interface TimelineData {
  date: string
  views: number
  unique_visitors: number
  avg_duration: number
}

export interface DeviceDistribution {
  device: string
  count: number
  percentage: number
}

export interface HourlyActivity {
  hour: number
  views: number
  avg_duration: number
}

export interface GeographicData {
  country: string
  country_code: string
  views: number
  unique_visitors: number
  percentage: number
}

/**
 * Get analytics summary for a preview (for preview owners)
 * This function is used for dashboard display and reporting
 */
export async function getPreviewAnalyticsSummary(previewId: string): Promise<AnalyticsSummary | null> {
  try {
    const { data, error } = await supabase.rpc('get_preview_analytics_summary', {
      p_preview_id: previewId
    })

    if (error) {
      console.error('Error fetching analytics summary:', error)
      return null
    }

    return data?.[0] || null
  } catch (error) {
    console.error('Error fetching analytics summary:', error)
    return null
  }
}

/**
 * Get timeline data for views over time chart
 */
export async function getPreviewTimelineData(previewId: string, days: number = 30): Promise<TimelineData[]> {
  try {
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    const { data, error } = await supabase
      .from('preview_analytics')
      .select('first_seen_at, total_duration_seconds, fingerprint_hash')
      .eq('preview_id', previewId)
      .gte('first_seen_at', startDate.toISOString())
      .lte('first_seen_at', endDate.toISOString())
      .order('first_seen_at', { ascending: true })

    if (error) throw error

    // Group by date
    const dateGroups = new Map<string, {
      views: number
      uniqueVisitors: Set<string>
      totalDuration: number
    }>()

    // Initialize all dates in range
    for (let i = 0; i < days; i++) {
      const date = new Date(startDate)
      date.setDate(date.getDate() + i)
      const dateStr = date.toISOString().split('T')[0]
      dateGroups.set(dateStr, {
        views: 0,
        uniqueVisitors: new Set(),
        totalDuration: 0
      })
    }

    // Process data
    data?.forEach(record => {
      const date = new Date(record.first_seen_at).toISOString().split('T')[0]
      const group = dateGroups.get(date)
      
      if (group) {
        group.views++
        if (record.fingerprint_hash) {
          group.uniqueVisitors.add(record.fingerprint_hash)
        }
        group.totalDuration += record.total_duration_seconds || 0
      }
    })

    // Convert to array
    return Array.from(dateGroups.entries()).map(([date, group]) => ({
      date,
      views: group.views,
      unique_visitors: group.uniqueVisitors.size,
      avg_duration: group.views > 0 ? Math.round(group.totalDuration / group.views) : 0
    }))
  } catch (error) {
    console.error('Error fetching timeline data:', error)
    return []
  }
}

/**
 * Get device distribution for pie chart
 */
export async function getPreviewDeviceDistribution(previewId: string): Promise<DeviceDistribution[]> {
  try {
    const { data, error } = await supabase
      .from('preview_analytics')
      .select('device_type')
      .eq('preview_id', previewId)
      .not('device_type', 'is', null)

    if (error) throw error

    const deviceCount = new Map<string, number>()
    let totalViews = 0

    data?.forEach(record => {
      // Normalize device type names
      let device = record.device_type || 'unknown'
      device = device.toLowerCase()
      
      // Map common variations to standard names
      if (device.includes('mobile') || device.includes('phone')) {
        device = 'mobile'
      } else if (device.includes('tablet') || device.includes('ipad')) {
        device = 'tablet'
      } else if (device.includes('desktop') || device.includes('computer')) {
        device = 'desktop'
      }
      
      deviceCount.set(device, (deviceCount.get(device) || 0) + 1)
      totalViews++
    })

    return Array.from(deviceCount.entries()).map(([device, count]) => ({
      device,
      count,
      percentage: totalViews > 0 ? Math.round((count / totalViews) * 100) : 0
    })).sort((a, b) => b.count - a.count)
  } catch (error) {
    console.error('Error fetching device distribution:', error)
    return []
  }
}

/**
 * Get hourly activity data
 */
export async function getPreviewHourlyActivity(previewId: string): Promise<HourlyActivity[]> {
  try {
    const { data, error } = await supabase
      .from('preview_analytics')
      .select('first_seen_at, total_duration_seconds')
      .eq('preview_id', previewId)
      .gte('first_seen_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())

    if (error) throw error

    const hourlyData = new Map<number, { views: number; totalDuration: number }>()

    // Initialize all hours
    for (let i = 0; i < 24; i++) {
      hourlyData.set(i, { views: 0, totalDuration: 0 })
    }

    data?.forEach(record => {
      const hour = new Date(record.first_seen_at).getHours()
      const existing = hourlyData.get(hour)!
      existing.views++
      existing.totalDuration += record.total_duration_seconds || 0
    })

    return Array.from(hourlyData.entries()).map(([hour, data]) => ({
      hour,
      views: data.views,
      avg_duration: data.views > 0 ? Math.round(data.totalDuration / data.views) : 0
    }))
  } catch (error) {
    console.error('Error fetching hourly activity:', error)
    return []
  }
}

/**
 * Get geographic distribution with enhanced data
 */
export async function getPreviewGeographicData(previewId: string): Promise<GeographicData[]> {
  try {
    const { data, error } = await supabase
      .from('preview_analytics')
      .select('country_code, fingerprint_hash')
      .eq('preview_id', previewId)
      .not('country_code', 'is', null)

    if (error) throw error

    const countryData = new Map<string, { views: number; uniqueVisitors: Set<string> }>()
    let totalViews = 0

    data?.forEach(record => {
      const country = record.country_code || 'Unknown'
      if (!countryData.has(country)) {
        countryData.set(country, { views: 0, uniqueVisitors: new Set() })
      }
      
      const existing = countryData.get(country)!
      existing.views++
      if (record.fingerprint_hash) {
        existing.uniqueVisitors.add(record.fingerprint_hash)
      }
      totalViews++
    })

    return Array.from(countryData.entries()).map(([country_code, data]) => ({
      country: country_code,
      country_code,
      views: data.views,
      unique_visitors: data.uniqueVisitors.size,
      percentage: totalViews > 0 ? Math.round((data.views / totalViews) * 100) : 0
    })).sort((a, b) => b.views - a.views)
  } catch (error) {
    console.error('Error fetching geographic data:', error)
    return []
  }
}

/**
 * Get detailed analytics records for a preview with pagination and filtering
 * Used for detailed analytics table display
 */
export async function getPreviewAnalyticsRecords(
  previewId: string,
  options: {
    page?: number
    pageSize?: number
    startDate?: Date
    endDate?: Date
    searchQuery?: string
  } = {}
) {
  try {
    const {
      page = 1,
      pageSize = 10,
      startDate,
      endDate,
      searchQuery
    } = options

    let query = supabase
      .from('preview_analytics')
      .select('*', { count: 'exact' })
      .eq('preview_id', previewId)
      .order('created_at', { ascending: false })

    // Apply date filters
    if (startDate) {
      const startOfDay = new Date(startDate)
      startOfDay.setHours(0, 0, 0, 0)
      query = query.gte('first_seen_at', startOfDay.toISOString())
    }
    if (endDate) {
      const endOfDay = new Date(endDate)
      endOfDay.setHours(23, 59, 59, 999)
      query = query.lte('first_seen_at', endOfDay.toISOString())
    }

    // Apply search filter
    if (searchQuery?.trim()) {
      const search = searchQuery.toLowerCase()
      query = query.or(
        `city.ilike.%${search}%,region.ilike.%${search}%,country_code.ilike.%${search}%,browser_name.ilike.%${search}%,device_type.ilike.%${search}%,operating_system.ilike.%${search}%`
      )
    }

    // Get total count first
    const { count } = await query

    // Get paginated data
    const { data, error } = await query
      .order('first_seen_at', { ascending: false })
      .range((page - 1) * pageSize, page * pageSize - 1)

    if (error) throw error

    return {
      data: data || [],
      totalCount: count || 0,
      totalPages: Math.ceil((count || 0) / pageSize)
    }
  } catch (error) {
    console.error('Error fetching analytics records:', error)
    throw error
  }
}

/**
 * Delete a single analytics record
 */
export async function deleteAnalyticsRecord(recordId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('preview_analytics')
      .delete()
      .eq('id', recordId)

    if (error) throw error
    return true
  } catch (error) {
    console.error('Error deleting analytics record:', error)
    return false
  }
}

/**
 * Delete multiple analytics records
 */
export async function deleteAnalyticsRecords(recordIds: string[]): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('preview_analytics')
      .delete()
      .in('id', recordIds)

    if (error) throw error
    return true
  } catch (error) {
    console.error('Error deleting analytics records:', error)
    return false
  }
}

/**
 * Delete all analytics records for a preview
 */
export async function deleteAllPreviewAnalytics(previewId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('preview_analytics')
      .delete()
      .eq('preview_id', previewId)

    if (error) throw error
    return true
  } catch (error) {
    console.error('Error deleting all analytics records:', error)
    return false
  }
}

/**
 * Get essential analytics data for key charts
 */
export async function getEssentialAnalytics(previewId: string): Promise<{
  timelineData: TimelineData[]
  deviceData: DeviceDistribution[]
  topCountries: Array<{ country: string; count: number; percentage: number }>
  peakHours: Array<{ hour: number; views: number }>
} | null> {
  try {
    const [timeline, devices, geographic, hourly] = await Promise.all([
      getPreviewTimelineData(previewId, 14), // Last 14 days
      getPreviewDeviceDistribution(previewId),
      getPreviewGeographicData(previewId),
      getPreviewHourlyActivity(previewId)
    ])

    // Get top 5 countries with percentages
    const totalViews = geographic.reduce((sum, item) => sum + item.views, 0)
    const topCountries = geographic.slice(0, 5).map(item => ({
      country: item.country,
      count: item.views,
      percentage: totalViews > 0 ? Math.round((item.views / totalViews) * 100) : 0
    }))

    // Get peak hours (top 6 hours)
    const peakHours = hourly
      .sort((a, b) => b.views - a.views)
      .slice(0, 6)
      .sort((a, b) => a.hour - b.hour)

    return {
      timelineData: timeline,
      deviceData: devices,
      topCountries,
      peakHours
    }
  } catch (error) {
    console.error('Error fetching essential analytics:', error)
    return null
  }
}
